import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, qs } from '../lib/api.js';
import { useAction, useResource } from '../lib/useApi.js';
import { useCan } from '../lib/auth.js';
import type {
  AssignmentRule,
  CoverageReport,
  DirectoryGroup,
  TargetType,
  TemplateSummary,
} from '../lib/types.js';
import {
  Banner,
  Card,
  Check,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  Pill,
  Stat,
  StatusPill,
  useToast,
} from '../components/ui.js';

/**
 * Assignment rules.
 *
 * The coverage panel is the point of this screen. A rule set that looks complete
 * can still miss people -- a contractor in no group, someone with a blank
 * department -- and a push that skips them reports as a clean success. Better to
 * find that here.
 */
export function RulesPage(): JSX.Element {
  const can = useCan();
  const toast = useToast();

  const rules = useResource<{ rules: AssignmentRule[]; supportedAttributes: string[] }>('/api/rules');
  const coverage = useResource<CoverageReport>('/api/rules/coverage');
  const templates = useResource<TemplateSummary[]>('/api/templates');
  const groups = useResource<{ groups: DirectoryGroup[] }>('/api/directory/groups');

  const [editing, setEditing] = useState<AssignmentRule | 'new' | null>(null);
  const [explainUpn, setExplainUpn] = useState('');

  const canWrite = can('rule:write');

  const toggle = useAction(async (rule: AssignmentRule) => {
    await api.patch(`/api/rules/${rule.id}`, { enabled: !rule.enabled });
    await Promise.all([rules.reload(), coverage.reload()]);
  });

  const remove = useAction(async (rule: AssignmentRule) => {
    await api.del(`/api/rules/${rule.id}`);
    await Promise.all([rules.reload(), coverage.reload()]);
    toast.success('Rule deleted', `"${rule.name}" no longer applies.`);
  });

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Assignment rules</h1>
          <p className="sub">
            Which template each person receives. The lowest priority number wins; at equal priority a
            more specific rule beats a broader one.
          </p>
        </div>
        <div className="page-actions">
          {canWrite && (
            <button className="primary" onClick={() => setEditing('new')}>
              New rule
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        {toggle.error && <ErrorState error={toggle.error} />}
        {remove.error && <ErrorState error={remove.error} />}
        {rules.error && <ErrorState error={rules.error} onRetry={rules.reload} />}

        {coverage.data && (
          <>
            {coverage.data.directoryUserCount === 0 ? (
              <Banner tone="warn">
                <strong>The directory snapshot is empty</strong>
                <div className="tiny">
                  Coverage cannot be calculated until a directory sync has run.{' '}
                  <Link to="/admin">Open the admin page</Link> to run one.
                </div>
              </Banner>
            ) : coverage.data.unassignedCount > 0 ? (
              <Banner tone="warn">
                <strong>
                  {coverage.data.unassignedCount} user
                  {coverage.data.unassignedCount === 1 ? '' : 's'} would receive no signature
                </strong>
                <div className="tiny">
                  No rule matches them, so a push silently skips them and still reports success. Add a
                  catch-all rule, or accept the gap deliberately.
                </div>
                <div className="tiny mono muted" style={{ marginTop: 4 }}>
                  {coverage.data.unassignedSample.join(', ')}
                  {coverage.data.unassignedCount > coverage.data.unassignedSample.length && ' …'}
                </div>
              </Banner>
            ) : (
              <Banner tone="ok">
                <strong>Every user is covered</strong>
                <div className="tiny">
                  All {coverage.data.total} users in the directory snapshot match at least one rule.
                </div>
              </Banner>
            )}

            <div className="grid cols-4 mb">
              <Stat label="Directory users" value={coverage.data.total} />
              <Stat
                label="Unassigned"
                value={coverage.data.unassignedCount}
                tone={coverage.data.unassignedCount > 0 ? 'warn' : 'ok'}
                note="Would receive nothing"
              />
              <Stat
                label="Decided by precedence"
                value={coverage.data.overlappingCount}
                note="Matched more than one rule"
              />
              <Stat label="Templates in use" value={coverage.data.byTemplate.length} />
            </div>
          </>
        )}

        <div className="grid cols-2">
          <Card title="Rules" tight>
            {rules.loading && !rules.data && (
              <div style={{ padding: 14 }}>
                <Loading rows={3} />
              </div>
            )}
            {rules.data && rules.data.rules.length === 0 && (
              <Empty
                title="No rules yet"
                action={
                  canWrite ? (
                    <button className="primary" onClick={() => setEditing('new')}>
                      Create a rule
                    </button>
                  ) : undefined
                }
              >
                Without a rule, nobody is assigned a template and a deployment has nothing to send.
              </Empty>
            )}
            {rules.data && rules.data.rules.length > 0 && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="num">Priority</th>
                      <th>Rule</th>
                      <th>Target</th>
                      <th>Template</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rules.data.rules.map((rule) => (
                      <tr key={rule.id} style={{ opacity: rule.enabled ? 1 : 0.5 }}>
                        <td className="num mono">{rule.priority}</td>
                        <td className="cell-strong">
                          {rule.name}
                          {!rule.enabled && <Pill tone="outline"> disabled</Pill>}
                        </td>
                        <td>
                          <Pill tone="accent">{rule.targetType.replace('_', ' ').toLowerCase()}</Pill>
                          {rule.targetValue && (
                            <div className="tiny mono muted">{rule.targetValue}</div>
                          )}
                        </td>
                        <td>
                          {rule.template ? (
                            <span className="row">
                              <Link to={`/templates/${rule.template.id}`}>{rule.template.name}</Link>
                              <StatusPill status={rule.template.status} />
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="right nowrap">
                          {canWrite && (
                            <>
                              <button className="sm" onClick={() => setEditing(rule)}>
                                Edit
                              </button>
                              <button
                                className="sm"
                                style={{ marginLeft: 4 }}
                                onClick={() => void toggle.run(rule)}
                              >
                                {rule.enabled ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                className="sm danger"
                                style={{ marginLeft: 4 }}
                                onClick={() => {
                                  if (window.confirm(`Delete the rule "${rule.name}"?`)) {
                                    void remove.run(rule);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="col">
            <Card title="Who gets what">
              {coverage.data?.byTemplate.length ? (
                <table className="data">
                  <tbody>
                    {coverage.data.byTemplate.map((row) => (
                      <tr key={row.templateId}>
                        <td>
                          <Link to={`/templates/${row.templateId}`}>{row.templateName}</Link>
                          {row.templateStatus && row.templateStatus !== 'PUBLISHED' && (
                            <Pill tone="warn"> {row.templateStatus.toLowerCase()}</Pill>
                          )}
                        </td>
                        <td className="num cell-strong">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="muted tiny">No users are assigned a template yet.</p>
              )}
            </Card>

            <ExplainCard upn={explainUpn} onUpn={setExplainUpn} />

            {coverage.data && coverage.data.overlappingSample.length > 0 && (
              <Card title="Decided by precedence">
                <p className="tiny muted">
                  These users matched more than one rule. Precedence chose the winner; if that is not
                  what you intended, adjust the priorities.
                </p>
                <table className="data">
                  <tbody>
                    {coverage.data.overlappingSample.map((row) => (
                      <tr key={row.upn}>
                        <td className="mono tiny">{row.upn}</td>
                        <td className="tiny">
                          <strong>{row.winner}</strong>
                          <div className="muted">over {row.alsoMatched.join(', ')}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          templates={templates.data ?? []}
          groups={groups.data?.groups ?? []}
          supportedAttributes={rules.data?.supportedAttributes ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void rules.reload();
            void coverage.reload();
          }}
        />
      )}
    </>
  );
}

function ExplainCard({ upn, onUpn }: { upn: string; onUpn: (v: string) => void }): JSX.Element {
  const [query, setQuery] = useState('');
  const result = useResource<{
    upn: string;
    displayName: string | null;
    department: string | null;
    groupCount: number;
    winner: { ruleName: string; templateName: string | null; priority: number } | null;
    alsoMatched: Array<{ ruleName: string; priority: number; templateName: string | null }>;
    explanation: string;
  }>(upn ? `/api/rules/explain${qs({ upn })}` : null);

  return (
    <Card title="Explain one user">
      <p className="tiny muted">
        Enter a UPN to see which rule wins for that person and what else matched.
      </p>
      <div className="row">
        <input
          value={query}
          placeholder="alex.mercer@contoso.com"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onUpn(query.trim());
          }}
        />
        <button onClick={() => onUpn(query.trim())} disabled={!query.trim()}>
          Explain
        </button>
      </div>

      {result.error && <ErrorState error={result.error} />}
      {result.data && (
        <div className="mt">
          <div className="cell-strong">{result.data.displayName ?? result.data.upn}</div>
          <div className="tiny muted mb">
            {result.data.department ?? 'no department'} · {result.data.groupCount} group(s)
          </div>
          <Banner tone={result.data.winner ? 'ok' : 'warn'}>{result.data.explanation}</Banner>
          {result.data.winner && (
            <div className="tiny">
              Receives <strong>{result.data.winner.templateName}</strong> via{' '}
              <strong>{result.data.winner.ruleName}</strong> (priority {result.data.winner.priority})
            </div>
          )}
          {result.data.alsoMatched.length > 0 && (
            <ul className="tiny muted" style={{ paddingLeft: 18, marginTop: 6 }}>
              {result.data.alsoMatched.map((r) => (
                <li key={r.ruleName}>
                  also matched {r.ruleName} (priority {r.priority}) → {r.templateName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function RuleModal({
  rule,
  templates,
  groups,
  supportedAttributes,
  onClose,
  onSaved,
}: {
  rule: AssignmentRule | null;
  templates: TemplateSummary[];
  groups: DirectoryGroup[];
  supportedAttributes: string[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [name, setName] = useState(rule?.name ?? '');
  const [templateId, setTemplateId] = useState(rule?.templateId ?? templates[0]?.id ?? '');
  const [targetType, setTargetType] = useState<TargetType>(rule?.targetType ?? 'GROUP');
  const [targetValue, setTargetValue] = useState(rule?.targetValue ?? '');
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);

  const save = useAction(async () => {
    const body = {
      name: name.trim(),
      templateId,
      targetType,
      targetValue: targetType === 'ALL_USERS' ? null : targetValue.trim(),
      priority,
      enabled,
    };
    if (rule) await api.patch(`/api/rules/${rule.id}`, body);
    else await api.post('/api/rules', body);
    toast.success(rule ? 'Rule updated' : 'Rule created');
    onSaved();
  });

  return (
    <Modal
      title={rule ? `Edit "${rule.name}"` : 'New assignment rule'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button
            className="primary"
            disabled={!name.trim() || !templateId || save.pending}
            onClick={() => void save.run()}
          >
            {save.pending ? 'Saving…' : 'Save rule'}
          </button>
        </>
      }
    >
      {save.error && <ErrorState error={save.error} />}

      <Field label="Rule name">
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>

      <Field label="Template">
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.status !== 'PUBLISHED' ? ` (${t.status.toLowerCase()})` : ''}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Applies to">
        <select value={targetType} onChange={(e) => setTargetType(e.target.value as TargetType)}>
          <option value="ALL_USERS">Everyone</option>
          <option value="GROUP">A security group</option>
          <option value="ATTRIBUTE">An attribute value</option>
          <option value="OU">An on-premises OU</option>
          <option value="USER">One person (for testing)</option>
        </select>
      </Field>

      {targetType === 'GROUP' && (
        <Field label="Group">
          <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
            <option value="">Choose a group…</option>
            {groups.map((g) => (
              <option key={g.entraObjectId} value={g.entraObjectId}>
                {g.displayName} ({g.memberCount})
              </option>
            ))}
          </select>
        </Field>
      )}

      {targetType === 'ATTRIBUTE' && (
        <Field
          label="Attribute expression"
          hint={`Format: attribute=value. Supported: ${supportedAttributes.join(', ')}.`}
        >
          <input
            value={targetValue}
            placeholder="department=Marketing"
            onChange={(e) => setTargetValue(e.target.value)}
            className="mono"
          />
        </Field>
      )}

      {targetType === 'OU' && (
        <Field
          label="Distinguished-name suffix"
          hint="Matched against onPremisesDistinguishedName. Cloud-only accounts never match an OU rule."
        >
          <input
            value={targetValue}
            placeholder="OU=Marketing,DC=contoso,DC=com"
            onChange={(e) => setTargetValue(e.target.value)}
            className="mono"
          />
        </Field>
      )}

      {targetType === 'USER' && (
        <Field label="User principal name">
          <input
            value={targetValue}
            placeholder="alex.mercer@contoso.com"
            onChange={(e) => setTargetValue(e.target.value)}
            className="mono"
          />
        </Field>
      )}

      <Field
        label="Priority"
        hint="Lower wins. Use 10 for narrow overrides and 500+ for a catch-all."
      >
        <input
          type="number"
          min={0}
          max={1000}
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value) || 0)}
        />
      </Field>

      <Check label="Rule is active" checked={enabled} onChange={setEnabled} />
    </Modal>
  );
}
